<template>
  <div
    v-if="recurrency"
    class="modal fade show d-block"
    tabindex="-1"
    style="background-color: rgba(0, 0, 0, 0.5)"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fa-solid fa-pen me-2 text-primary"></i>
            Editar recorrência
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Programação -->
          <label class="form-label fw-bold">Programação</label>
          <div class="row g-2 mb-3">
            <div class="col-12 col-md-4">
              <label class="form-label small text-muted">Frequência</label>
              <select class="form-select" v-model="form.freqOpt">
                <option
                  v-for="opt in frequencyOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div class="col-6 col-md-5" v-if="isWeekly">
              <label class="form-label small text-muted">Dia da semana</label>
              <select class="form-select" v-model.number="form.weekday">
                <option v-for="(w, i) in weekdays" :key="i" :value="i">
                  {{ w }}
                </option>
              </select>
            </div>
            <div class="col-6 col-md-5" v-else>
              <label class="form-label small text-muted">Dia do mês</label>
              <input
                type="number"
                class="form-control"
                v-model.number="form.monthday"
                min="1"
                max="31"
              />
            </div>
            <div class="col-6 col-md-3">
              <label class="form-label small text-muted">Horário</label>
              <input type="time" class="form-control" v-model="form.time" />
            </div>
          </div>

          <!-- Valor -->
          <div class="mb-3">
            <label class="form-label fw-bold">Valor por sessão (R$)</label>
            <input
              type="number"
              class="form-control"
              :class="{ 'is-invalid': valueBelowMin }"
              v-model.number="form.value"
              min="50"
              step="0.01"
            />
            <div v-if="valueBelowMin" class="invalid-feedback d-block">
              O valor mínimo permitido é R$ 50,00.
            </div>
          </div>

          <div v-if="bigValueChange" class="alert alert-warning py-2 small mb-3">
            <i class="fa-solid fa-triangle-exclamation me-1"></i>
            Variação de <strong>{{ changePctFormatted }}</strong> em relação ao
            valor atual. Confirme se está correto.
          </div>

          <!-- Vigência -->
          <div class="mb-3">
            <label class="form-label fw-bold">Início das mudanças</label>
            <input
              type="date"
              class="form-control"
              v-model="form.effective_from"
              :min="today"
            />
          </div>

          <div v-if="scheduleChanged" class="alert alert-warning py-2 small mb-0">
            <i class="fa-solid fa-triangle-exclamation me-1"></i>
            Sessões futuras <strong>não pagas</strong> a partir dessa data serão
            canceladas e regeradas seguindo o novo cronograma. Sessões já pagas
            são mantidas.
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!canSubmit || submitting"
            @click="submit"
          >
            <span v-if="submitting">
              <i class="fa-solid fa-spinner fa-spin me-1"></i> Salvando...
            </span>
            <span v-else>
              <i class="fa-solid fa-check me-1"></i> Salvar alterações
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "RecurrencyEditModal",
  props: {
    // Quando não-nulo, a modal abre e carrega os dados dessa recorrência
    recurrency: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      submitting: false,
      original: {},
      form: {
        freqOpt: "weekly-1",
        weekday: 1,
        monthday: 1,
        time: "09:00",
        value: null,
        effective_from: "",
      },
      frequencyOptions: [
        { value: "weekly-1", label: "Semanal", frequency: "weekly", interval: 1 },
        { value: "weekly-2", label: "Quinzenal", frequency: "weekly", interval: 2 },
        { value: "monthly-1", label: "Mensal", frequency: "monthly", interval: 1 },
      ],
      weekdays: [
        "Domingo",
        "Segunda-feira",
        "Terça-feira",
        "Quarta-feira",
        "Quinta-feira",
        "Sexta-feira",
        "Sábado",
      ],
    };
  },
  computed: {
    today() {
      return this.$moment().format("YYYY-MM-DD");
    },
    isWeekly() {
      return this.form.freqOpt.startsWith("weekly");
    },
    valueBelowMin() {
      return this.form.value != null && this.form.value < 50;
    },
    changePct() {
      const current = parseFloat(this.original.value);
      const next = parseFloat(this.form.value);
      if (!current || !next) return 0;
      return ((next - current) / current) * 100;
    },
    bigValueChange() {
      return Math.abs(this.changePct) >= 20;
    },
    changePctFormatted() {
      const pct = this.changePct;
      return `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`;
    },
    valueChanged() {
      return Number(this.form.value) !== Number(this.original.value);
    },
    scheduleChanged() {
      if (this.form.freqOpt !== this.original.freqOpt) return true;
      if (this.form.time !== this.original.time) return true;
      if (this.isWeekly) return this.form.weekday !== this.original.weekday;
      return this.form.monthday !== this.original.monthday;
    },
    canSubmit() {
      if (this.form.value < 50) return false;
      if (!this.form.effective_from) return false;
      return this.valueChanged || this.scheduleChanged;
    },
  },
  watch: {
    recurrency: {
      immediate: true,
      handler(rec) {
        if (rec) this.initForm(rec);
      },
    },
  },
  methods: {
    initForm(rec) {
      const freqOpt =
        this.frequencyOptions.find(
          (o) => o.frequency === rec.frequency && o.interval === rec.interval
        )?.value || "weekly-1";

      const ref = rec.reference_date
        ? this.$moment(rec.reference_date)
        : this.$moment();

      this.original = {
        value: parseFloat(rec.value) || 0,
        freqOpt,
        weekday: ref.day(),
        monthday: ref.date(),
        time: ref.format("HH:mm"),
      };

      this.form = {
        freqOpt,
        weekday: ref.day(),
        monthday: ref.date(),
        time: ref.format("HH:mm"),
        value: parseFloat(rec.value) || null,
        effective_from: this.$moment().add(2, "days").format("YYYY-MM-DD"),
      };
    },
    selectedFreq() {
      return this.frequencyOptions.find((o) => o.value === this.form.freqOpt);
    },
    buildReferenceDate() {
      const base = this.$moment(this.form.effective_from).startOf("day");
      const [h, m] = this.form.time.split(":").map(Number);
      let d;
      if (this.isWeekly) {
        d = base.clone().day(this.form.weekday);
        if (d.isBefore(base, "day")) d.add(7, "days");
      } else {
        d = base.clone().date(this.form.monthday);
        if (d.isBefore(base, "day")) d.add(1, "month").date(this.form.monthday);
      }
      d.hour(h).minute(m).second(0);
      return d.format("YYYY-MM-DD HH:mm:ss");
    },
    async submit() {
      const payload = { effective_from: this.form.effective_from };
      if (this.valueChanged) payload.value = this.form.value;
      if (this.scheduleChanged) {
        const freq = this.selectedFreq();
        payload.frequency = freq.frequency;
        payload.interval = freq.interval;
        payload.reference_date = this.buildReferenceDate();
      }

      let confirmMsg = "Confirmar as alterações da recorrência?";
      if (this.scheduleChanged) {
        confirmMsg +=
          "\n\nSessões futuras não pagas serão canceladas e regeradas no novo cronograma.";
      }
      if (!confirm(confirmMsg)) return;

      this.submitting = true;
      try {
        await this.$appointmentsService.network.put(
          `api/appointment-recurrencies/${this.recurrency.id}`,
          payload
        );
        this.$eventBus.$emit("showToast", {
          title: "Recorrência atualizada",
          msg: "As alterações foram aplicadas com sucesso.",
        });
        this.$emit("saved");
      } catch (error) {
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: error.message || "Erro ao atualizar recorrência.",
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
